import React, { useState, useMemo } from 'react';
import { Form, Select, Input, Alert, Space, Typography, Tag, List, Radio } from 'antd';
import { trpc } from '../../../../lib/trpc';
import { withDevModalFilePath, NumericInput } from '../../../../components/common';

const { Text, Paragraph } = Typography;
const { Modal } = withDevModalFilePath('client/src/pages/admin/subscriptions/components/CreatePromotionCodesModal.tsx');

interface CouponOption {
  id: string;
  name: string | null;
  percentOff: number | null;
  amountOff: number | null;
  duration: string;
  durationInMonths: number | null;
}

interface CreatePromotionCodesModalProps {
  open: boolean;
  coupons: CouponOption[];
  onClose: () => void;
  onSuccess: (result: { created: unknown[]; errors: unknown[] }) => void;
}

interface PromotionCodesFormValues {
  couponId: string;
  generationMode: 'random' | 'sequential';
  codePrefix: string;
  startNumber?: number;
  randomCodeLength?: number;
  maxRedemptions?: number;
  count: number;
  discountType?: string;
}

export function CreatePromotionCodesModal({
  open,
  coupons,
  onClose,
  onSuccess,
}: CreatePromotionCodesModalProps) {
  const [form] = Form.useForm();
  const [codePrefix, setCodePrefix] = useState('WELCOME50-');
  const [generationMode, setGenerationMode] = useState<'sequential' | 'random'>('random');
  const [startNumber, setStartNumber] = useState(1);
  const [randomCodeLength, setRandomCodeLength] = useState(6);
  const [, setMaxRedemptions] = useState(1);
  const [count, setCount] = useState(20);
  const [selectedCouponId, setSelectedCouponId] = useState<string | undefined>(undefined);

  const createMutation = trpc.stripeCouponAdmin.createPromotionCodesBatch.useMutation({
    onSuccess: (result) => {
      form.resetFields();
      onSuccess({
        created: result.created ?? [],
        errors: result.errors ?? [],
      });
    },
    onError: (error) => {
      console.error('Failed to create promotion codes:', error);
    },
  });

  // 選択されたクーポンを取得
  const selectedCoupon = coupons.find((c) => c.id === selectedCouponId);

  // 選択されたクーポンから割引タイプを自動生成
  const suggestedDiscountType = React.useMemo(() => {
    if (!selectedCoupon) return '';

    if (selectedCoupon.percentOff) {
      return `${selectedCoupon.percentOff}_percent_off`;
    }
    if (selectedCoupon.amountOff) {
      return `${selectedCoupon.amountOff}_amount_off`;
    }
    return '';
  }, [selectedCoupon]);

  // クーポン選択時にプレフィックスと割引タイプを自動設定
  const handleCouponChange = (couponId: string) => {
    setSelectedCouponId(couponId);

    const coupon = coupons.find((c) => c.id === couponId);
    if (coupon?.percentOff) {
      // 割引率からプレフィックスを生成
      const prefix = `WELCOME${coupon.percentOff}-`;
      setCodePrefix(prefix);
      form.setFieldValue('codePrefix', prefix);

      // 割引タイプを自動設定
      const discountType = `${coupon.percentOff}_percent_off`;
      form.setFieldValue('discountType', discountType);
    }
  };

  // ランダムコード生成（プレビュー用）
  const generateRandomCodePreview = (length: number): string => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  };

  // プレビュー生成
  const preview = useMemo(() => {
    if (!codePrefix || count < 1) return [];

    const codes: string[] = [];

    if (generationMode === 'random') {
      // ランダムモード: サンプルを5件生成
      const previewCount = Math.min(5, count);
      for (let i = 0; i < previewCount; i++) {
        const randomPart = generateRandomCodePreview(randomCodeLength);
        codes.push(`${codePrefix}${randomPart}`);
      }

      if (count > 5) {
        codes.push('...');
        codes.push(`${codePrefix}[ランダム]`);
      }
    } else {
      // 連番モード: 既存のロジック
      const maxNumber = startNumber + count - 1;
      const digits = Math.max(3, maxNumber.toString().length);

      // 最初の5件
      const previewCount = Math.min(5, count);
      for (let i = 0; i < previewCount; i++) {
        const number = startNumber + i;
        const paddedNumber = number.toString().padStart(digits, '0');
        codes.push(`${codePrefix}${paddedNumber}`);
      }

      // ...を追加
      if (count > 5) {
        codes.push('...');
      }

      // 最後の1件
      if (count > 5) {
        const lastNumber = startNumber + count - 1;
        const paddedNumber = lastNumber.toString().padStart(digits, '0');
        codes.push(`${codePrefix}${paddedNumber}`);
      }
    }

    return codes;
  }, [codePrefix, generationMode, startNumber, randomCodeLength, count]);

  const handleSubmit = async (values: PromotionCodesFormValues) => {
    await createMutation.mutateAsync({
      couponId: values.couponId,
      codePrefix: values.codePrefix,
      codeGenerationMode: values.generationMode,
      startNumber: values.generationMode === 'sequential' ? values.startNumber : undefined,
      randomCodeLength: values.generationMode === 'random' ? values.randomCodeLength : undefined,
      maxRedemptions: values.maxRedemptions,
      count: values.count,
      metadata: {
        avs_coupon_tag: 'onboarding_discount',
        discount_type: values.discountType || undefined,
      },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setCodePrefix('WELCOME50-');
    setGenerationMode('random');
    setStartNumber(1);
    setRandomCodeLength(6);
    setMaxRedemptions(1);
    setCount(20);
    setSelectedCouponId(undefined);
    onClose();
  };

  return (
    <Modal
      title="プロモーションコード一括作成"
      open={open}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={createMutation.isPending}
      width={700}
      okText="作成"
      cancelText="キャンセル"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          generationMode: 'random',
          codePrefix: 'WELCOME50-',
          startNumber: 1,
          randomCodeLength: 6,
          maxRedemptions: 1,
          count: 20,
        }}
      >
        <Alert
          message="セキュリティ推奨"
          description={
            <Space direction="vertical" size="small">
              <Paragraph style={{ margin: 0 }}>
                本番環境では<Text strong>ランダム生成</Text>を推奨します。連番形式は推測可能で不正利用のリスクがあります。
              </Paragraph>
            </Space>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item
          name="couponId"
          label="クーポン選択"
          rules={[{ required: true, message: 'クーポンを選択してください' }]}
        >
          <Select
            placeholder="クーポンを選択"
            showSearch
            optionFilterProp="children"
            onChange={handleCouponChange}
          >
            {coupons.map((coupon) => (
              <Select.Option key={coupon.id} value={coupon.id}>
                {coupon.name || coupon.id}
                {' - '}
                {coupon.percentOff
                  ? `${coupon.percentOff}%`
                  : coupon.amountOff
                    ? `¥${coupon.amountOff}`
                    : ''}
                {' × '}
                {coupon.duration === 'once'
                  ? '1回'
                  : coupon.duration === 'forever'
                    ? '永続'
                    : `${coupon.durationInMonths}ヶ月`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="generationMode"
          label="コード生成方式"
          rules={[{ required: true, message: '生成方式を選択してください' }]}
        >
          <Radio.Group onChange={(e) => setGenerationMode(e.target.value)}>
            <Space direction="vertical">
              <Radio value="random">
                <Space direction="vertical" size={0}>
                  <Text strong>ランダム生成（推奨）</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    推測不可能なコード。例: WELCOME50-A3X9K
                  </Text>
                </Space>
              </Radio>
              <Radio value="sequential">
                <Space direction="vertical" size={0}>
                  <Text>連番生成（管理用）</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    推測可能。例: WELCOME50-001（テスト・管理用途のみ）
                  </Text>
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="codePrefix"
          label="コードプレフィックス"
          rules={[
            { required: true, message: 'プレフィックスを入力してください' },
            { max: 20, message: '20文字以内で入力してください' },
            {
              pattern: /^[A-Z0-9-]+$/i,
              message: '英数字とハイフンのみ使用できます',
            },
          ]}
          extra="例: WELCOME50- （末尾にハイフンを付けると見やすくなります）"
        >
          <Input
            placeholder="WELCOME50-"
            onChange={(e) => setCodePrefix(e.target.value.toUpperCase())}
          />
        </Form.Item>

        <Space style={{ width: '100%' }} size="middle">
          {generationMode === 'sequential' && (
            <Form.Item
              name="startNumber"
              label="開始番号"
              rules={[
                { required: true, message: '開始番号を入力してください' },
                { type: 'number', min: 1, message: '1以上の値を入力してください' },
              ]}
              style={{ flex: 1 }}
            >
              <NumericInput
                style={{ width: '100%' }}
                min={1}
                placeholder="1"
                onChange={(value) => {
                  const next = typeof value === 'number' ? value : Number(value);
                  setStartNumber(next || 1);
                }}
              />
            </Form.Item>
          )}

          {generationMode === 'random' && (
            <Form.Item
              name="randomCodeLength"
              label="ランダムコード長"
              rules={[
                { required: true, message: 'コード長を入力してください' },
                { type: 'number', min: 4, max: 12, message: '4〜12文字で入力してください' },
              ]}
              extra="推奨: 6〜8文字"
              style={{ flex: 1 }}
            >
              <NumericInput
                style={{ width: '100%' }}
                min={4}
                max={12}
                placeholder="6"
                onChange={(value) => {
                  const next = typeof value === 'number' ? value : Number(value);
                  setRandomCodeLength(next || 6);
                }}
              />
            </Form.Item>
          )}

          <Form.Item
            name="count"
            label="作成個数"
            rules={[
              { required: true, message: '作成個数を入力してください' },
              {
                type: 'number',
                min: 1,
                max: 100,
                message: '1〜100の範囲で入力してください',
              },
            ]}
            style={{ flex: 1 }}
          >
          <NumericInput
            style={{ width: '100%' }}
            min={1}
            max={100}
            placeholder="20"
            onChange={(value) => {
              const next = typeof value === 'number' ? value : Number(value);
              setCount(next || 20);
            }}
          />
        </Form.Item>
        </Space>

        <Form.Item
          name="maxRedemptions"
          label="使用回数制限"
          rules={[
            { required: true, message: '使用回数制限を入力してください' },
            { type: 'number', min: 1, message: '1以上の値を入力してください' },
          ]}
          extra="デフォルト: 1回のみ使用可能（推奨）。無制限にする場合は大きな数値を設定してください。"
        >
          <NumericInput
            style={{ width: '100%' }}
            min={1}
            placeholder="1"
            addonAfter="回まで"
          />
        </Form.Item>

        <Form.Item
          name="discountType"
          label="割引タイプ（メタデータ用）"
          extra={
            suggestedDiscountType
              ? `選択したクーポンから自動設定されました。必要に応じて編集できます。`
              : '管理用のタグです。任意で入力できます。'
          }
        >
          <Input
            placeholder={suggestedDiscountType || '例: 50_percent_off'}
            disabled={!selectedCouponId}
          />
        </Form.Item>

        {preview.length > 0 && (
          <Alert
            message="プレビュー"
            description={
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {generationMode === 'random'
                    ? '以下はサンプルです。実際のコードは作成時にランダム生成されます:'
                    : '以下のコードが作成されます（最初の5件と最後の1件を表示）:'}
                </Text>
                <List
                  size="small"
                  style={{ marginTop: 8 }}
                  dataSource={preview}
                  renderItem={(item) => (
                    <List.Item style={{ padding: '4px 0' }}>
                      {item === '...' || item.includes('[ランダム]') ? (
                        <Text type="secondary">{item}</Text>
                      ) : (
                        <Tag color={generationMode === 'random' ? 'green' : 'blue'}>{item}</Tag>
                      )}
                    </List.Item>
                  )}
                />
                <Space direction="vertical" size="small" style={{ marginTop: 8, width: '100%' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    合計: {count}個のコードが作成されます
                  </Text>
                  {generationMode === 'random' && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ※ ランダムコードは推測不可能で安全です（混同しやすい文字 O,0,I,1,L は除外）
                    </Text>
                  )}
                </Space>
              </div>
            }
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        {createMutation.error && (
          <Alert
            message="エラー"
            description={createMutation.error.message}
            type="error"
            showIcon
            closable
            style={{ marginTop: 16 }}
          />
        )}
      </Form>
    </Modal>
  );
}
